import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface Column<T> {
	key: string;
	header: string;
	sortable?: boolean;
	render?: (item: T) => ReactNode;
}

interface DataTableProps<T> {
	columns: Column<T>[];
	data: T[];
	isLoading?: boolean;
	emptyMessage?: string;
	searchPlaceholder?: string;
	searchFilter?: (item: T, query: string) => boolean;
	onRowClick?: (item: T) => void;
	pageSize?: number;
}

export function DataTable<T extends Record<string, unknown>>({
	columns,
	data,
	isLoading,
	emptyMessage,
	searchPlaceholder,
	searchFilter,
	onRowClick,
	pageSize = 20,
}: DataTableProps<T>) {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [sortKey, setSortKey] = useState<string | null>(null);
	const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
	const [page, setPage] = useState(0);

	const filtered = useMemo(() => {
		if (!search || !searchFilter) return data;
		return data.filter((item) => searchFilter(item, search));
	}, [data, search, searchFilter]);

	const sorted = useMemo(() => {
		if (!sortKey) return filtered;
		return [...filtered].sort((a, b) => {
			const aVal = a[sortKey];
			const bVal = b[sortKey];
			if (aVal == null && bVal == null) return 0;
			if (aVal == null) return 1;
			if (bVal == null) return -1;
			const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
			return sortDir === "asc" ? cmp : -cmp;
		});
	}, [filtered, sortKey, sortDir]);

	const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
	const paginated = sorted.slice(page * pageSize, (page + 1) * pageSize);

	function handleSort(key: string) {
		if (sortKey === key) {
			setSortDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setSortKey(key);
			setSortDir("asc");
		}
	}

	function SortIcon({ columnKey }: { columnKey: string }) {
		if (sortKey !== columnKey) return <ArrowUpDown className="ml-1 inline h-3 w-3 text-muted" />;
		return sortDir === "asc" ? (
			<ArrowUp className="ml-1 inline h-3 w-3 text-accent" />
		) : (
			<ArrowDown className="ml-1 inline h-3 w-3 text-accent" />
		);
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				{searchFilter && <Skeleton className="h-10 w-64" />}
				<div className="rounded-xl border border-white/10 bg-bg-elevated">
					<Table>
						<TableHeader>
							<TableRow>
								{columns.map((col) => (
									<TableHead key={col.key}>{col.header}</TableHead>
								))}
							</TableRow>
						</TableHeader>
						<TableBody>
							{Array.from({ length: 5 }).map((_, i) => (
								<TableRow key={`skeleton-${i}`}>
									{columns.map((col) => (
										<TableCell key={col.key}>
											<Skeleton className="h-5 w-full" />
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{searchFilter && (
				<div className="relative w-64">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
					<Input
						placeholder={searchPlaceholder ?? t("admin.search")}
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(0);
						}}
						className="pl-9"
					/>
				</div>
			)}

			<div className="rounded-xl border border-white/10 bg-bg-elevated">
				<Table>
					<TableHeader>
						<TableRow>
							{columns.map((col) => (
								<TableHead key={col.key}>
									{col.sortable ? (
										<button
											type="button"
											onClick={() => handleSort(col.key)}
											className="inline-flex items-center font-medium hover:text-primary"
										>
											{col.header}
											<SortIcon columnKey={col.key} />
										</button>
									) : (
										col.header
									)}
								</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{paginated.length === 0 ? (
							<TableRow>
								<TableCell colSpan={columns.length} className="py-12 text-center text-secondary">
									{emptyMessage ?? t("admin.noResults")}
								</TableCell>
							</TableRow>
						) : (
							paginated.map((item, index) => (
								<TableRow
									key={(item.id as string) ?? index}
									onClick={onRowClick ? () => onRowClick(item) : undefined}
									className={cn(onRowClick && "cursor-pointer")}
								>
									{columns.map((col) => (
										<TableCell key={col.key}>
											{col.render ? col.render(item) : (item[col.key] as ReactNode)}
										</TableCell>
									))}
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>

			{totalPages > 1 && (
				<div className="flex items-center justify-between px-2">
					<p className="text-sm text-secondary">
						{sorted.length} {t("admin.totalItems")}
					</p>
					<div className="flex items-center gap-2">
						<Button
							variant="outline"
							size="sm"
							disabled={page === 0}
							onClick={() => setPage((p) => p - 1)}
						>
							{t("admin.previous")}
						</Button>
						<span className="text-sm text-secondary">
							{page + 1} / {totalPages}
						</span>
						<Button
							variant="outline"
							size="sm"
							disabled={page >= totalPages - 1}
							onClick={() => setPage((p) => p + 1)}
						>
							{t("admin.next")}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
